import React, {useEffect, useState} from 'react';
import {CircularProgress, Tooltip} from '@mui/material';
import {History, Person, Warning} from '@mui/icons-material';
import api from '../../../api/axios';
import {format} from 'date-fns';
import BaseModal from '../../utils/BaseModal';

interface SkinDbOwnerHistoryDTO {
  hasPremium: boolean;
  steamProfileName: string | null;
  steamAvatarUrl: string | null;
  steamUrl: string | null;
  itemFirstSeen: string | null;
}

interface OwnerHistoryModalProps {
  open: boolean;
  onClose: () => void;
  assetId: string | null;
}

const OwnerHistoryModal = ({ open, onClose, assetId }: OwnerHistoryModalProps) => {
  const [history, setHistory] = useState<SkinDbOwnerHistoryDTO[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && assetId) {
      fetchHistory();
    }
  }, [open, assetId]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/item/${assetId}/owner-history`);
      // Sort history by date descending
      const sorted = response.data.sort((a: SkinDbOwnerHistoryDTO, b: SkinDbOwnerHistoryDTO) => {
        if (!a.itemFirstSeen || !b.itemFirstSeen) return 0;
        return new Date(b.itemFirstSeen).getTime() - new Date(a.itemFirstSeen).getTime();
      });
      setHistory(sorted);
    } catch (error) {
      console.error('Error fetching owner history:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
      <BaseModal
          open={open}
          onAbort={onClose}
          maxWidth="xs"
          titleNode={
            <div className="flex items-center gap-2">
              <History className="text-blue-500" sx={{ fontSize: 20 }} />
              <span className="text-sm font-black uppercase tracking-tighter dark:text-white">
            Owner History
          </span>
            </div>
          }
      >
        <div className="py-2">
          {loading ? (
              <div className="flex justify-center py-10">
                <CircularProgress size={24} className="text-blue-500" />
              </div>
          ) : history.length > 0 ? (
              <div className="relative">
                {/* Vertical timeline line */}
                <div className="absolute left-6 top-0 bottom-0 w-px bg-neutral-200 dark:bg-neutral-800" />

                <div className="flex flex-col gap-8">
                  {history.map((entry, idx) => (
                      <div key={idx} className="relative flex items-start gap-4">
                        <div className="relative z-10">
                          {entry.hasPremium && entry.steamAvatarUrl ? (
                              <img
                                  src={entry.steamAvatarUrl}
                                  alt="avatar"
                                  className="w-12 h-12 rounded ring-2 ring-blue-500/20 bg-neutral-800 object-cover"
                              />
                          ) : (
                              <div className="w-12 h-12 rounded bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center border border-neutral-200 dark:border-neutral-700">
                                <Person className="text-neutral-400" />
                              </div>
                          )}
                        </div>

                        <div className="flex flex-col pt-1">
                          <div className="flex items-center gap-2">
                            {entry.hasPremium ? (
                                <a
                                    href={entry.steamUrl || '#'}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="text-sm font-bold dark:text-white hover:text-blue-500 transition-colors no-underline"
                                >
                                  {entry.steamProfileName}
                                </a>
                            ) : (
                                <span className="text-sm font-bold text-neutral-500 italic">
                          Private Owner
                        </span>
                            )}
                            {idx === 0 && (
                                <span className="text-[9px] bg-emerald-500/10 text-emerald-500 px-1.5 py-0.5 font-black uppercase rounded">
                          Current
                        </span>
                            )}
                          </div>

                          <span className="text-[11px] font-mono text-neutral-400">
                      {entry.itemFirstSeen ? format(new Date(entry.itemFirstSeen), 'dd.MM.yyyy HH:mm') : 'Unknown Date'}
                    </span>

                          {!entry.hasPremium && (
                              <div className="flex items-center gap-1 mt-1">
                                <Tooltip title="Detailed info only for Premium items" arrow>
                          <span className="text-[9px] text-amber-500/80 uppercase font-bold flex items-center gap-0.5">
                            <Warning sx={{ fontSize: 10 }} /> Limited Data
                          </span>
                                </Tooltip>
                              </div>
                          )}
                        </div>
                      </div>
                  ))}
                </div>
              </div>
          ) : (
              <div className="text-center py-10 text-neutral-500 text-xs uppercase font-bold">
                No history records found
              </div>
          )}
        </div>
      </BaseModal>
  );
};

export default OwnerHistoryModal;